import { getCookie, getSearchParam } from '@/util/url'
import { IGif } from '@giphy/js-types'
import getAdTestResponse from './ad-mocks/ad-test-response'
import { processDecision } from './ads-util'
import { giphyLocalUrl, publicRuntimeConfig } from './env'

export type AdEndpointViews =
    // spotlights that appear in a grid
    | 'xl_carousel_banner'
    | 'home_video_spotlight'
    | 'search_results_video_spotlight'
    | 'gif_details_video_spotlight'
    | 'homepage_banner' // bouncer banner
    // geo views
    | 'home' // geo home page banner
    | 'gif_details' // gif detail banner
    | 'search_results' // search page banner
    | 'home_grid_0' // geo home grid
    | 'gif_details_grid_0' // geo gif details grid
    | 'search_results_grid_0' // geo search grid

export const gridPathnameMap: Record<string, AdEndpointViews> = {
    '/gifs': 'gif_details_grid_0',
    '/search': 'search_results_grid_0',
    '/': 'home_grid_0',
}

export type AdDecisionAnalytics = {
    start?: string
    firstq?: string
    midpoint?: string
    thirdq?: string
    complete?: string
    mute?: string
    unmute?: string
    pause?: string
    rewind?: string
    resume?: string
    // Allow unexpected/extra event names without losing type safety for known ones
    [eventName: string]: string | undefined
}

export type AdDecision = {
    gif: IGif
    subdecisions?: AdDecision[]
    cta_text?: string
    cta_url?: string
    impression_url?: string
    click_url?: string
    analytics?: AdDecisionAnalytics
    is_clickable?: boolean
}

export type AdResponse = {
    decisions: Record<string, AdDecision | null>
}

export const hasAnyAd = (adResponse?: AdResponse) => {
    const decisions = adResponse?.decisions || {}
    return Object.keys(decisions).some((key) => {
        return decisions[key] !== null
    })
}

export const getAdDecisionByType = (response: AdResponse | undefined, id?: AdEndpointViews) =>
    id ? (response?.decisions?.[id] as AdDecision) : undefined

export const getClickableAd = (response: AdResponse | undefined, id: AdEndpointViews) => {
    const decision = getAdDecisionByType(response, id)
    if (decision?.is_clickable) {
        return decision
    } else {
        return undefined
    }
}

export const hasAd = (type: AdEndpointViews, adResponse?: AdResponse) => {
    return !!getAdDecisionByType(adResponse, type)
}

export const mergeResponses = (response1?: AdResponse | undefined, response2?: AdResponse): AdResponse => {
    return {
        decisions: { ...(response1?.decisions || {}), ...(response2?.decisions || {}) },
    }
}

export type AdEndpointRequestOptions = {
    searchParams?: URLSearchParams
    views: AdEndpointViews[]
    rating?: string
    keywords?: string[]
    cookie?: string
    accessToken?: string
    platform?: string
    headers?: HeadersInit
}

export type AdEndpointRequestBody = {
    keywords: string[]
    rating: string
    views: AdEndpointViews[]
    platform: string
    testing: boolean
    use_mock: boolean
}

/**
 * Fetch from the ads endpoint, this needs to be invoked from both the server and the client
 */
export const fetchAdFromEndpoint = async ({
    searchParams,
    views,
    rating = 'unrated',
    keywords = [],
    accessToken = '',
    cookie = '',
    platform = 'web',
    headers: incomingHeaders,
}: AdEndpointRequestOptions): Promise<[AdResponse?, string?]> => {
    const body: AdEndpointRequestBody = {
        keywords,
        rating,
        views,
        platform,
        testing: getCookie('ads_dev_network', cookie) === 'true',
        use_mock: getCookie('ads_use_mock', cookie) === 'true',
    }

    // load test1.json here if there is a searchParam that equals ad_test=1
    const adTestValue = getAdTestValue(searchParams)
    if (!isNaN(adTestValue)) {
        return await getAdTestResponse(adTestValue)
    }
    if (publicRuntimeConfig.isPlaywright) {
        return [, 'Running Playwright test']
    }
    const fetchUrl = `${giphyLocalUrl}/api/v2/ads/`
    try {
        let headers: HeadersInit = {
            'content-type': 'application/json',
            ...(accessToken ? { authorization: `Token ${accessToken}` } : {}),
            cookie,
            'X-CSRFToken': getCookie('csrftoken', cookie),
        }
        if (incomingHeaders) {
            // filter out content-length header
            headers = {
                ...headers,
                ...Object.fromEntries(Object.entries(incomingHeaders).filter(([key]) => key !== 'content-length')),
            }
        }
        // console.log(`ad endpoint request ${fetchUrl}`, JSON.stringify(body), headers)
        const response = await fetch(fetchUrl, {
            method: 'POST',
            body: JSON.stringify(body),
            headers,
        })
        if (response.ok) {
            try {
                const result = (await response.json()) as AdResponse
                // console.log('ads-endpoint response', result)
                // For each decision, if click_url and gif exist, set gif.bottle_data?.tdata?.click_out_url = click_url
                // Also append `ap=1` to every URL under gif.images for ad attribution
                let processedDecisions: Record<string, AdDecision | null> = {}
                processedDecisions = Object.fromEntries(
                    Object.entries(result?.decisions || {}).map(([key, decision]) => {
                        const newDecision = decision ? processDecision(decision) : null
                        return [key, newDecision] as [string, AdDecision | null]
                    })
                )

                return [{ decisions: processedDecisions }]
            } catch (jsonError) {
                console.error('Failed to parse ad endpoint response as JSON', jsonError)
                return [, `Status:${response.status} ${jsonError}`]
            }
        }
        let text = ''
        try {
            text = await response.text()
        } catch (_e) {}
        return [, `Status:${response.status} ${text}`]
    } catch (e) {
        console.error(e)
        return [, e as any]
    }
}

export function getAdTestValue(searchParams: URLSearchParams | undefined) {
    return parseInt(getSearchParam('ad_test', searchParams) || '')
}
